import React, { useState, useEffect, useMemo } from "react";
import Select from "react-select";
import {
  Card,
  CardBody,
  Col,
  Container,
  Form,
  Input,
  Label,
  Row,
  Button,
  Table,
  FormGroup,
  Nav,
  NavItem,
  NavLink,
  TabContent,
  TabPane,
  Modal,
  ModalHeader,
  ModalBody,
  ModalFooter,
} from "reactstrap";
import { 
  getNextReceiptNumber, 
  getCustomerCreditInvoices, 
  saveReceipt, 
  getReceipts,
  getReceiptById
} from "../../components/Api/GmsReceipt";
import { checkDayClosing } from "../../components/Api/GmsDayClosing";
import { getBusinesses } from "../../components/Api/GmsBusiness";
import { getBranches } from "../../components/Api/GmsBranch";
import { searchCustomers } from "../../components/Api/GmsCustomer";
import { Eye } from "lucide-react";
import { ClipLoader } from "react-spinners";
import Swal from "sweetalert2";
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import { 
  hasPagePermissionWithAdmin, 
  hasViewOnlyPermission, 
  hasEditOrHigherPermission,
  isUserAdmin 
} from "../../components/Permission/PermissionService";
import Breadcrumb from "../../components/Common/Breadcrumb";
import { formatDateForAPI, formatDateForDisplay } from "../../helpers/dateHelper";

// Custom styles for Select components (from PayrollProcessing)
const selectStyles = {
  control: (provided, state) => ({
    ...provided,
    minHeight: '38px',
    borderColor: state.isFocused ? '#556ee6' : '#ced4da',
    boxShadow: state.isFocused ? '0 0 0 0.2rem rgba(85, 110, 230, 0.25)' : 'none',
  }),
  menu: (provided) => ({
    ...provided,
    zIndex: 9999,
    maxHeight: 200,
  }),
  menuList: (provided) => ({
    ...provided,
    maxHeight: 180,
  }),
  option: (provided, state) => ({
    ...provided,
    backgroundColor: state.isSelected ? '#556ee6' : state.isFocused ? '#f8f9fa' : 'white',
    color: state.isSelected ? 'white' : '#495057',
    '&:hover': {
      backgroundColor: state.isSelected ? '#556ee6' : '#e9ecef',
    },
  }),
};

// Session management
const getUserSession = () => {
  const authUser = JSON.parse(localStorage.getItem("authUser") || "{}");
  const user = JSON.parse(localStorage.getItem("user") || "{}");
  return {
    userId: authUser.userId || 1,
    userName: authUser.userName || "System",
    companyId: authUser.companyId || user.user_Company_Id || null,
    branchId: authUser.branchId || user.user_Branch_Id || null,
  };
};

const defaultForm = {
  ReceiptId: 0,
  ReceiptNumber: "",
  ReceiptDate: new Date(),
  Amount: 0,
  PaymentType: "ca",
  AuthCode: "",
  Type: "cu",
  Is_Advance: false,
  BusinessId: null,
  BranchId: null,
  CustomerId: null,
  CreatedBy: 1,
  ReceiptDetails: [],
};

const emptyFilter = {
  ReceiptId: null,
  CustomerId: null,
  BusinessId: null,
  BranchId: null,
  FromDate: null,
  ToDate: null,
  PaymentType: null,
};

export default function CustomerReceiptsPage() {
  const [form, setForm] = useState(defaultForm);
  const [filter, setFilter] = useState(emptyFilter);
  const [businesses, setBusinesses] = useState([]);
  const [branches, setBranches] = useState([]);
  const [creditInvoices, setCreditInvoices] = useState([]);
  const [paymentAllocation, setPaymentAllocation] = useState([]);
  const [receipts, setReceipts] = useState([]);
  const [loading, setLoading] = useState(false);
  const [saveLoading, setSaveLoading] = useState(false);
  const [customerSearchOptions, setCustomerSearchOptions] = useState([]);
  const [customerSearchLoading, setCustomerSearchLoading] = useState(false);
  const [selectedCustomerOption, setSelectedCustomerOption] = useState(null);
  const [searchTimeout, setSearchTimeout] = useState(null);
  const [showFilters, setShowFilters] = useState(false);
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [totalCount, setTotalCount] = useState(0);
  const [viewLoadingRow, setViewLoadingRow] = useState(null);
  const [activeTab, setActiveTab] = useState("1");
  const [showReceiptModal, setShowReceiptModal] = useState(false);
  const [receiptDetails, setReceiptDetails] = useState(null);
  const [loadingReceiptDetails, setLoadingReceiptDetails] = useState(false);
  const [isReadOnly, setIsReadOnly] = useState(false);

  // Permission states
  const permissions = JSON.parse(localStorage.getItem('permissions') || '[]');
  const hasPermission = hasPagePermissionWithAdmin(permissions, '/GmsCustomerReceipts');
  const isViewOnly = hasViewOnlyPermission(permissions, '/GmsCustomerReceipts');
  const hasEditOrHigher = hasEditOrHigherPermission(permissions, '/GmsCustomerReceipts');
  const isAdmin = isUserAdmin();

  // Get user session
  const session = getUserSession();

  // Check if business/branch should be readonly
  const isBusinessReadonly = !!session.companyId;
  const isBranchReadonly = !!session.branchId;

  useEffect(() => {
    if (hasPermission) {
      loadInitialData();
      loadReceipts();
      
      // Set business/branch from session if available
      if (session.companyId) {
        setForm(prev => ({ ...prev, BusinessId: session.companyId }));
      }
      if (session.branchId) {
        setForm(prev => ({ ...prev, BranchId: session.branchId }));
      }
    } else {
      window.location.href = '/Dashboard';
    }
  }, [hasPermission]);

  // Check day closing after business/branch is set from session
  useEffect(() => {
    if (hasPermission) {
      // Small delay to ensure form state is updated
      const timer = setTimeout(() => {
        checkCurrentDateDayClosing();
      }, 100);
      
      return () => clearTimeout(timer);
    }
  }, [hasPermission, session.companyId, session.branchId]);

  // Check if current date is closed/holiday and previous day closing
  const checkCurrentDateDayClosing = async () => {
    try {
      const today = new Date();
      today.setHours(0, 0, 0, 0);
      
      // Only check if we have business info
      const businessId = session.companyId || form.BusinessId;
      const branchId = session.branchId || form.BranchId || null;
      
      if (businessId) {
        // Check current date
        const checkRequest = {
          transactionDate: formatDateForAPI(today, false),
          businessId: businessId,
          branchId: branchId
        };
        
        const dayClosingCheck = await checkDayClosing(checkRequest);
        
        if (dayClosingCheck.isClosed) {
          const message = dayClosingCheck.isHoliday 
            ? "Holiday has been marked for today. Please come back tomorrow."
            : "Day closing has been marked for today. Please come back tomorrow.";
          
          Swal.fire({
            icon: 'warning',
            title: 'Day Closed',
            text: message,
            confirmButtonText: 'OK'
          });
          
          // Disable the form
          setIsReadOnly(true);
          return;
        }
        
        // Check previous day closing
        const yesterday = new Date(today);
        yesterday.setDate(yesterday.getDate() - 1);
        
        const previousDayCheck = await checkDayClosing({
          transactionDate: formatDateForAPI(yesterday, false),
          businessId: businessId,
          branchId: branchId
        });
        
        if (!previousDayCheck.isClosed) {
          const yesterdayStr = formatDateForDisplay(yesterday, 'date');
          Swal.fire({
            icon: 'warning',
            title: 'Previous Day Not Closed',
            text: `Day closing is not done for ${yesterdayStr}. Please close the previous day first before creating any transactions.`,
            confirmButtonText: 'OK'
          });
          
          // Disable the form
          setIsReadOnly(true);
        }
      }
    } catch (error) {
      console.error("Error checking day closing on page load:", error);
      // Don't block page load if check fails
    }
  };

  // Re-check day closing when business/branch becomes available
  useEffect(() => {
    const businessId = session.companyId || form.BusinessId;
    
    // Only check if we have business info and haven't already disabled the form
    if (businessId && !isReadOnly) {
      // Small delay to ensure state is updated
      const timer = setTimeout(() => {
        checkCurrentDateDayClosing();
      }, 200);
      
      return () => clearTimeout(timer);
    }
  }, [form.BusinessId, form.BranchId, businesses.length]);

  // Load receipt number when business is selected
  useEffect(() => {
    if (form.BusinessId) {
      loadNextReceiptNumber();
    }
  }, [form.BusinessId]);

  // Load credit invoices when customer is selected
  useEffect(() => {
    if (form.CustomerId && form.BusinessId) {
      loadCreditInvoices();
    } else {
      setCreditInvoices([]);
      setPaymentAllocation([]);
    }
  }, [form.CustomerId, form.BusinessId, form.BranchId]);

  // Auto-allocate payment when amount changes
  useEffect(() => {
    if (form.Amount > 0 && creditInvoices.length > 0) {
      allocatePayment(form.Amount);
    } else {
      setPaymentAllocation([]);
    }
  }, [form.Amount, creditInvoices]);

  const loadInitialData = async () => {
    try {
      await Promise.all([
        loadBusinesses(),
        loadBranches(),
      ]);
    } catch (error) {
      console.error("Error loading initial data:", error);
    }
  };

  const loadBusinesses = async () => {
    try {
      const data = await getBusinesses({ status: true });
      setBusinesses(data || []);
    } catch (error) {
      console.error("Error loading businesses:", error);
    }
  };

  const loadBranches = async () => {
    try {
      const data = await getBranches({ branch_Active: true });
      setBranches(data || []);
    } catch (error) {
      console.error("Error loading branches:", error);
    }
  };

  const loadNextReceiptNumber = async () => {
    try {
      const response = await getNextReceiptNumber(form.BusinessId);
      if (response.isSuccess) {
        setForm(prev => ({ ...prev, ReceiptNumber: response.nextReceiptNumber }));
      }
    } catch (error) {
      console.error("Error loading next receipt number:", error);
    }
  };

  const loadCreditInvoices = async () => {
    try {
      setLoading(true);
      const invoices = await getCustomerCreditInvoices(
        form.CustomerId,
        form.BusinessId,
        form.BranchId
      );
      setCreditInvoices(invoices || []);
    } catch (error) {
      console.error("Error loading credit invoices:", error);
      Swal.fire("Error", "Failed to load credit invoices", "error");
    } finally {
      setLoading(false);
    }
  };

  const searchCustomersManually = async (inputValue) => {
    if (!inputValue || inputValue.length < 3) {
      setCustomerSearchOptions([]);
      return;
    }
    
    try {
      setCustomerSearchLoading(true);
      const response = await searchCustomers(inputValue, 1, 20);
      
      const formattedOptions = response.items.map(customer => ({
        value: customer.customer_Id,
        label: `${customer.customer_NameEn || 'N/A'} — ${customer.customer_ContactNo || 'N/A'}`,
        customer: customer
      }));
      
      setCustomerSearchOptions(formattedOptions);
    } catch (error) {
      console.error('Error searching customers:', error);
      setCustomerSearchOptions([]);
    } finally {
      setCustomerSearchLoading(false);
    }
  };

  // Auto-allocate payment using FIFO (oldest invoice first)
  const allocatePayment = (amount) => {
    if (!creditInvoices || creditInvoices.length === 0) {
      setPaymentAllocation([]);
      return;
    }

    let remainingAmount = amount;
    const allocation = [];

    // Sort invoices by date (oldest first)
    // API returns: invoice_Id, invoice_No, invoice_Date, invoice_NetAmount, paidAmount, outstandingAmount
    const sortedInvoices = [...creditInvoices].sort((a, b) => 
      new Date(a.invoice_Date || a.Invoice_Date) - new Date(b.invoice_Date || b.Invoice_Date)
    );

    for (const invoice of sortedInvoices) {
      if (remainingAmount <= 0) break;

      // Handle both camelCase and PascalCase from API
      const outstanding = invoice.outstandingAmount || invoice.OutstandingAmount || 0;
      const paidInThisReceipt = Math.min(remainingAmount, outstanding);
      // Round to 2 decimal places to avoid floating point precision issues
      const paidAmountRounded = Math.round(paidInThisReceipt * 100) / 100;
      const previousPaid = invoice.paidAmount || invoice.PaidAmount || 0;
      const settledAfter = previousPaid + paidAmountRounded;
      const invoiceId = invoice.invoice_Id || invoice.Invoice_Id;
      const invoiceNo = invoice.invoice_No || invoice.Invoice_No;
      const invoiceDate = invoice.invoice_Date || invoice.Invoice_Date;
      const invoiceNetAmount = invoice.invoice_NetAmount || invoice.Invoice_NetAmount;

      allocation.push({
        InvoiceId: invoiceId,
        Invoice_No: invoiceNo,
        Invoice_Date: invoiceDate,
        InvoiceAmount: invoiceNetAmount,
        OutstandingBefore: outstanding,
        PaidAmount: paidAmountRounded,
        OutstandingAfter: Math.round((outstanding - paidAmountRounded) * 100) / 100,
        TotalAmount: outstanding, // Outstanding before this payment
        SettledAmount: Math.round(settledAfter * 100) / 100, // Cumulative settled after this payment
      });

      remainingAmount -= paidAmountRounded;
    }

    setPaymentAllocation(allocation);
  };

  // Update allocation amount manually
  const updateAllocationAmount = (index, newAmount) => {
    const allocation = [...paymentAllocation];
    const item = allocation[index];
    const maxAmount = item.OutstandingBefore;
    
    // Round to 2 decimal places to avoid floating point precision issues
    const roundedAmount = Math.round(newAmount * 100) / 100;

    if (roundedAmount > maxAmount) {
      Swal.fire({
        icon: 'warning',
        title: 'Invalid Amount',
        text: `Amount cannot exceed outstanding amount (${maxAmount.toFixed(2)})`,
      });
      return;
    }

    item.PaidAmount = roundedAmount;
    item.OutstandingAfter = Math.round((item.OutstandingBefore - roundedAmount) * 100) / 100;
    const invoice = creditInvoices.find(inv => (inv.invoice_Id || inv.Invoice_Id) === item.InvoiceId);
    const previousPaid = invoice ? (invoice.paidAmount || invoice.PaidAmount || 0) : 0;
    item.SettledAmount = Math.round((previousPaid + roundedAmount) * 100) / 100;

    // Recalculate remaining allocation
    const totalAllocated = allocation.reduce((sum, a) => sum + a.PaidAmount, 0);
    // Use tolerance of 0.01 for floating point comparison
    if (totalAllocated > form.Amount + 0.01) {
      Swal.fire({
        icon: 'warning',
        title: 'Invalid Amount',
        text: `Total allocated amount (${totalAllocated.toFixed(2)}) cannot exceed payment amount (${form.Amount.toFixed(2)})`,
      });
      return;
    }

    setPaymentAllocation(allocation);
  };

  const handleFormChange = (field, value) => {
    setForm(prev => ({ ...prev, [field]: value }));
  };

  const handleSave = async () => {
    try {
      // Validation
      if (!form.BusinessId) {
        Swal.fire("Error", "Please select a business", "error");
        return;
      }
      if (!form.CustomerId) {
        Swal.fire("Error", "Please select a customer", "error");
        return;
      }
      if (!form.Amount || form.Amount <= 0) {
        Swal.fire("Error", "Please enter a valid payment amount", "error");
        return;
      }
      if (!form.PaymentType) {
        Swal.fire("Error", "Please select a payment method", "error");
        return;
      }

      // Check for overpayment
      const totalAllocated = paymentAllocation.reduce((sum, a) => sum + a.PaidAmount, 0);
      const totalOutstanding = creditInvoices.reduce((sum, inv) => sum + inv.OutstandingAmount, 0);

      if (creditInvoices.length > 0 && totalAllocated > totalOutstanding) {
        Swal.fire({
          icon: 'error',
          title: 'Overpayment Detected',
          text: `Payment amount (${totalAllocated.toFixed(2)}) exceeds total outstanding amount (${totalOutstanding.toFixed(2)}). Please adjust the payment allocation.`,
        });
        return;
      }

      // Check if payment amount matches allocation
      if (creditInvoices.length > 0 && Math.abs(totalAllocated - form.Amount) > 0.01) {
        Swal.fire({
          icon: 'warning',
          title: 'Amount Mismatch',
          text: `Total allocated amount (${totalAllocated.toFixed(2)}) does not match payment amount (${form.Amount.toFixed(2)}). Please adjust the allocation.`,
        });
        return;
      }

      // Check day closing before saving
      if (form.BusinessId && form.ReceiptDate) {
        try {
          const receiptDate = new Date(form.ReceiptDate);
          const checkRequest = {
            transactionDate: formatDateForAPI(receiptDate, false),
            businessId: form.BusinessId,
            branchId: form.BranchId || null
          };
          
          const dayClosingCheck = await checkDayClosing(checkRequest);
          
          if (dayClosingCheck.isClosed) {
            const message = dayClosingCheck.isHoliday 
              ? "Holiday has been marked for this date. Please come back tomorrow."
              : "Day closing has been marked for this date. Please come back tomorrow.";
            Swal.fire("Day Closed", message, "warning");
            return;
          }
          
          // Check previous day closing - always check if creating for current or future date
          const today = new Date();
          today.setHours(0, 0, 0, 0);
          const receiptDateOnly = new Date(receiptDate);
          receiptDateOnly.setHours(0, 0, 0, 0);
          
          // If receipt date is today or future, check if previous day is closed
          if (receiptDateOnly >= today) {
            const previousDate = new Date(receiptDateOnly);
            previousDate.setDate(previousDate.getDate() - 1);
            
            const previousDayCheck = await checkDayClosing({
              transactionDate: formatDateForAPI(previousDate, false),
              businessId: form.BusinessId,
              branchId: form.BranchId || null
            });
            
            if (!previousDayCheck.isClosed) {
              Swal.fire("Previous Day Not Closed", `Day closing is not done for ${formatDateForDisplay(previousDate, 'date')}. Please close the previous day first.`, "warning");
              return;
            }
          }
        } catch (error) {
          console.error("Error checking day closing:", error);
          // Continue with save if check fails (don't block due to API error)
        }
      }

      setSaveLoading(true);

      // Combine selected date with current time (client's local timezone)
      // This ensures the time reflects when the user is creating the receipt in their timezone
      const selectedDate = form.ReceiptDate instanceof Date ? form.ReceiptDate : new Date(form.ReceiptDate);
      const now = new Date();
      const receiptDateTime = new Date(
        selectedDate.getFullYear(),
        selectedDate.getMonth(),
        selectedDate.getDate(),
        now.getHours(),
        now.getMinutes(),
        now.getSeconds()
      );

      const receiptData = {
        ReceiptId: form.ReceiptId,
        ReceiptNumber: form.ReceiptNumber,
        ReceiptDate: formatDateForAPI(receiptDateTime, true), // Selected date + current time (client timezone)
        Amount: form.Amount,
        PaymentType: form.PaymentType,
        AuthCode: (form.PaymentType === "cc" || form.PaymentType === "on") && form.AuthCode ? form.AuthCode : null,
        Type: "cu",
        Is_Advance: creditInvoices.length === 0 || paymentAllocation.length === 0,
        BusinessId: form.BusinessId,
        BranchId: form.BranchId,
        CustomerId: form.CustomerId,
        CreatedBy: session.userId,
        ModifiedBy: null,
        ReceiptDetails: paymentAllocation.map(a => ({
          InvoiceId: a.InvoiceId,
          InvoiceAmount: Math.round((a.InvoiceAmount || 0) * 100) / 100,
          PaidAmount: Math.round((a.PaidAmount || 0) * 100) / 100,
          TotalAmount: Math.round((a.TotalAmount || 0) * 100) / 100,
          SettledAmount: Math.round((a.SettledAmount || 0) * 100) / 100,
        })),
      };

      const result = await saveReceipt(receiptData);

      if (result.isSuccess) {
        Swal.fire({
          icon: 'success',
          title: 'Success',
          text: result.message || 'Receipt saved successfully',
        });
        
        // Reset form
        setForm({
          ...defaultForm,
          BusinessId: session.companyId || null,
          BranchId: session.branchId || null,
          ReceiptDate: new Date(),
        });
        setPaymentAllocation([]);
        setCreditInvoices([]);
        setSelectedCustomerOption(null);
        loadReceipts();
        loadNextReceiptNumber();
      } else {
        Swal.fire("Error", result.message || "Failed to save receipt", "error");
      }
    } catch (error) {
      console.error("Error saving receipt:", error);
      const errorMessage = error.response?.data?.message || error.message || "Failed to save receipt";
      Swal.fire("Error", errorMessage, "error");
    } finally {
      setSaveLoading(false);
    }
  };

  const loadReceipts = async () => {
    try {
      setLoading(true);
      // Build filter data, excluding null values
      const filterData = {
        Type: "cu",
        Page: page,
        PageSize: pageSize,
      };
      
      // Only add non-null filter values
      if (filter.ReceiptId) filterData.ReceiptId = filter.ReceiptId;
      if (filter.CustomerId) filterData.CustomerId = filter.CustomerId;
      if (filter.BusinessId) filterData.BusinessId = filter.BusinessId;
      if (filter.BranchId) filterData.BranchId = filter.BranchId;
      if (filter.FromDate) filterData.FromDate = formatDateForAPI(filter.FromDate, false);
      if (filter.ToDate) filterData.ToDate = formatDateForAPI(filter.ToDate, false);
      if (filter.PaymentType) filterData.PaymentType = filter.PaymentType;
      
      const response = await getReceipts(filterData);
      setReceipts(response.receipts || []);
      setTotalCount(response.totalCount || 0);
    } catch (error) {
      console.error("Error loading receipts:", error);
    } finally {
      setLoading(false);
    }
  };

  const handleView = async (receipt) => {
    try {
      // Handle both camelCase and PascalCase from API
      const receiptId = receipt.receiptId || receipt.ReceiptId;
      
      setViewLoadingRow(receiptId);
      setLoadingReceiptDetails(true);
      setShowReceiptModal(true);
      
      // Use receipt data from list (has customer/business/branch names) and fetch details
      const receiptData = await getReceiptById(receiptId);
      
      console.log("Receipt from list:", receipt);
      console.log("Receipt details from API:", receiptData);
      
      // Merge receipt data from list (has names) with details from API
      const mergedReceipt = {
        ...receipt, // This has customer_NameEn, business_NameEn, branch_NameEn
        ...receiptData, // This has ReceiptDetails
        // Override with API data if available, but keep names from list
        receiptDetails: receiptData?.receiptDetails || receiptData?.ReceiptDetails || []
      };
      
      console.log("Merged receipt data:", mergedReceipt);
      console.log("Receipt details array:", mergedReceipt.receiptDetails);
      
      setReceiptDetails(mergedReceipt);
    } catch (error) {
      console.error("Error viewing receipt:", error);
      Swal.fire("Error", "Failed to load receipt details", "error");
      setShowReceiptModal(false);
    } finally {
      setViewLoadingRow(null);
      setLoadingReceiptDetails(false);
    }
  };

  const handlePageChange = (newPage) => {
    setPage(newPage);
  };

  useEffect(() => {
    loadReceipts();
  }, [page, pageSize, filter]);

  const businessOptions = useMemo(() => {
    return businesses.map(b => ({
      value: b.business_Id,
      label: b.business_NameEn || b.business_Name,
    }));
  }, [businesses]);

  const branchOptions = useMemo(() => {
    if (!form.BusinessId) return [];
    return branches
      .filter(b => b.branch_Buisness_Id === form.BusinessId)
      .map(b => ({
        value: b.branch_Id,
        label: `${b.branch_Code || ''} - ${b.branch_NameEn || ''}`.trim(),
      }));
  }, [branches, form.BusinessId]);

  const paymentMethodOptions = [
    { value: "ca", label: "Cash" },
    { value: "cc", label: "Credit Card" },
    { value: "on", label: "Online" },
  ];

  const formatDate = (date) => {
    return formatDateForDisplay(date, 'date');
  };

  const formatCurrency = (amount) => {
    return parseFloat(amount || 0).toFixed(2);
  };

  if (!hasPermission) {
    return null;
  }

  return (
    <React.Fragment>
      <div className="page-content">
        <Container fluid>
          <Breadcrumb title="Customer Receipts" breadcrumbItem="Customer Receipts" />
          
          <Row>
            <Col xs={12}>
              <Card>
                <CardBody className="p-0">
                  <Nav tabs className="nav-tabs-custom">
                    {hasEditOrHigher && (
                      <NavItem>
                        <NavLink
                          style={{ 
                            cursor: "pointer",
                            fontWeight: activeTab === "1" ? "bold" : "normal",
                            color: activeTab === "1" ? "#556ee6" : "#74788d",
                            borderBottom: activeTab === "1" ? "2px solid #556ee6" : "none",
                            padding: "15px 25px",
                            fontSize: "16px",
                            display: "flex",
                            alignItems: "center",
                            gap: "8px"
                          }}
                          className={activeTab === "1" ? "active" : ""}
                          onClick={() => setActiveTab("1")}
                        >
                          <i className="bx bx-money"></i>
                          <span>Enter Payment</span>
                        </NavLink>
                      </NavItem>
                    )}
                    <NavItem>
                      <NavLink
                        style={{ 
                          cursor: "pointer",
                          fontWeight: activeTab === "2" ? "bold" : "normal",
                          color: activeTab === "2" ? "#556ee6" : "#74788d",
                          borderBottom: activeTab === "2" ? "2px solid #556ee6" : "none",
                          padding: "15px 25px",
                          fontSize: "16px",
                          display: "flex",
                          alignItems: "center",
                          gap: "8px"
                        }}
                        className={activeTab === "2" ? "active" : ""}
                        onClick={() => setActiveTab("2")}
                      >
                        <i className="bx bx-list-ul"></i>
                        <span>Receipts List</span>
                      </NavLink>
                    </NavItem>
                  </Nav>
                  
                  <TabContent activeTab={activeTab} className="p-4">
                    <TabPane tabId="1">
                      <div>
                        {/* Form Section */}
                        {hasEditOrHigher && (
                          <Card className="mb-4">
                      <CardBody>
                        <h5 className="card-title mb-3">Enter Payment</h5>
                        <Form>
                          <Row>
                            <Col md={3}>
                              <FormGroup>
                                <Label>Business <span className="text-danger">*</span></Label>
                                <Select
                                  value={businessOptions.find(opt => opt.value === form.BusinessId) || null}
                                  onChange={(option) => {
                                    handleFormChange("BusinessId", option?.value || null);
                                    handleFormChange("BranchId", null);
                                  }}
                                  options={businessOptions}
                                  isClearable={!isBusinessReadonly}
                                  isDisabled={isBusinessReadonly || isReadOnly}
                                  placeholder="Select Business"
                                  styles={selectStyles}
                                />
                              </FormGroup>
                            </Col>
                            <Col md={3}>
                              <FormGroup>
                                <Label>Branch</Label>
                                <Select
                                  value={branchOptions.find(opt => opt.value === form.BranchId) || null}
                                  onChange={(option) => handleFormChange("BranchId", option?.value || null)}
                                  options={branchOptions}
                                  isClearable={!isBranchReadonly}
                                  isDisabled={isBranchReadonly || !form.BusinessId || isReadOnly}
                                  placeholder="Select Branch"
                                  styles={selectStyles}
                                />
                              </FormGroup>
                            </Col>
                            <Col md={3}>
                              <FormGroup>
                                <Label>Receipt Number</Label>
                                <Input
                                  type="text"
                                  value={form.ReceiptNumber}
                                  readOnly
                                  style={{ backgroundColor: '#f8f9fa' }}
                                />
                              </FormGroup>
                            </Col>
                            <Col md={3}>
                              <FormGroup>
                                <Label>Receipt Date <span className="text-danger">*</span></Label>
                                <DatePicker
                                  selected={form.ReceiptDate}
                                  onChange={(date) => handleFormChange("ReceiptDate", date)}
                                  dateFormat="dd/MM/yyyy"
                                  className="form-control"
                                  maxDate={new Date()}
                                  disabled={isReadOnly}
                                />
                              </FormGroup>
                            </Col>
                          </Row>

                          <Row>
                            <Col md={6}>
                              <FormGroup>
                                <Label>Customer <span className="text-danger">*</span></Label>
                                <Select
                                  value={selectedCustomerOption}
                                  onChange={(option) => {
                                    setSelectedCustomerOption(option);
                                    handleFormChange("CustomerId", option?.value || null);
                                  }}
                                  options={customerSearchOptions}
                                  placeholder="Type at least 3 characters to search customers..."
                                  styles={selectStyles}
                                  isClearable
                                  isSearchable
                                  isLoading={customerSearchLoading}
                                  isDisabled={isReadOnly}
                                  noOptionsMessage={() => "Type at least 3 characters to search"}
                                  loadingMessage={() => "Searching customers..."}
                                  onInputChange={(inputValue) => {
                                    if (isReadOnly) return inputValue;
                                    if (searchTimeout) {
                                      clearTimeout(searchTimeout);
                                    }
                                    const timeout = setTimeout(() => {
                                      searchCustomersManually(inputValue);
                                    }, 300);
                                    setSearchTimeout(timeout);
                                    return inputValue;
                                  }}
                                />
                              </FormGroup>
                            </Col>
                          </Row>

                          {/* Credit Invoices Table */}
                          {creditInvoices.length > 0 && (
                            <Row className="mt-3">
                              <Col xs={12}>
                                <h6 className="mb-2">Credit Invoices</h6>
                                <div className="table-responsive">
                                  <Table bordered size="sm">
                                    <thead className="table-light">
                                      <tr>
                                        <th>Invoice No</th>
                                        <th>Date</th>
                                        <th>Net Amount</th>
                                        <th>Paid Amount</th>
                                        <th>Outstanding</th>
                                      </tr>
                                    </thead>
                                    <tbody>
                                      {creditInvoices.map((inv) => {
                                        // Handle both camelCase and PascalCase from API
                                        const invoiceId = inv.invoice_Id || inv.Invoice_Id;
                                        const invoiceNo = inv.invoice_No || inv.Invoice_No || 'N/A';
                                        const invoiceDate = inv.invoice_Date || inv.Invoice_Date;
                                        const netAmount = inv.invoice_NetAmount || inv.Invoice_NetAmount || 0;
                                        const paidAmount = inv.paidAmount || inv.PaidAmount || 0;
                                        const outstanding = inv.outstandingAmount || inv.OutstandingAmount || 0;
                                        
                                        return (
                                          <tr key={invoiceId}>
                                            <td>{invoiceNo}</td>
                                            <td>{formatDate(invoiceDate)}</td>
                                            <td>{formatCurrency(netAmount)}</td>
                                            <td>{formatCurrency(paidAmount)}</td>
                                            <td><strong>{formatCurrency(outstanding)}</strong></td>
                                          </tr>
                                        );
                                      })}
                                    </tbody>
                                  </Table>
                                </div>
                              </Col>
                            </Row>
                          )}

                          {creditInvoices.length === 0 && form.CustomerId && (
                            <Row className="mt-3">
                              <Col xs={12}>
                                <div className="alert alert-info">
                                  No outstanding credit invoices found for this customer.
                                  This payment will be recorded as an advance.
                                </div>
                              </Col>
                            </Row>
                          )}

                          <Row className="mt-3">
                            <Col md={4}>
                              <FormGroup>
                                <Label>Payment Amount <span className="text-danger">*</span></Label>
                                <Input
                                  type="number"
                                  step="0.01"
                                  min="0"
                                  value={form.Amount || ''}
                                  onChange={(e) => handleFormChange("Amount", parseFloat(e.target.value) || 0)}
                                  placeholder="Enter payment amount"
                                  disabled={isReadOnly}
                                />
                              </FormGroup>
                            </Col>
                            <Col md={4}>
                              <FormGroup>
                                <Label>Payment Method <span className="text-danger">*</span></Label>
                                <Select
                                  value={paymentMethodOptions.find(opt => opt.value === form.PaymentType) || null}
                                  onChange={(option) => handleFormChange("PaymentType", option?.value || "ca")}
                                  options={paymentMethodOptions}
                                  styles={selectStyles}
                                  isDisabled={isReadOnly}
                                />
                              </FormGroup>
                            </Col>
                            {(form.PaymentType === "cc" || form.PaymentType === "on") && (
                              <Col md={4}>
                                <FormGroup>
                                  <Label>Authentication Code</Label>
                                  <Input
                                    type="text"
                                    value={form.AuthCode}
                                    onChange={(e) => handleFormChange("AuthCode", e.target.value)}
                                    placeholder="Enter transaction/reference number"
                                  />
                                </FormGroup>
                              </Col>
                            )}
                          </Row>

                          {/* Payment Allocation Table */}
                          {paymentAllocation.length > 0 && (
                            <Row className="mt-3">
                              <Col xs={12}>
                                <h6 className="mb-2">Payment Allocation</h6>
                                <div className="table-responsive">
                                  <Table bordered size="sm">
                                    <thead className="table-light">
                                      <tr>
                                        <th>Invoice No</th>
                                        <th>Outstanding</th>
                                        <th>Allocated Amount</th>
                                        <th>Remaining</th>
                                      </tr>
                                    </thead>
                                    <tbody>
                                      {paymentAllocation.map((alloc, index) => (
                                        <tr key={alloc.InvoiceId}>
                                          <td>{alloc.Invoice_No}</td>
                                          <td>{formatCurrency(alloc.OutstandingBefore)}</td>
                                          <td>
                                            <Input
                                              type="number"
                                              step="0.01"
                                              min="0"
                                              max={alloc.OutstandingBefore}
                                              value={alloc.PaidAmount || ''}
                                              onChange={(e) => updateAllocationAmount(index, parseFloat(e.target.value) || 0)}
                                              style={{ width: '120px' }}
                                            />
                                          </td>
                                          <td>{formatCurrency(alloc.OutstandingAfter)}</td>
                                        </tr>
                                      ))}
                                    </tbody>
                                    <tfoot>
                                      <tr>
                                        <th colSpan="2">Total Allocated</th>
                                        <th>{formatCurrency(paymentAllocation.reduce((sum, a) => sum + a.PaidAmount, 0))}</th>
                                        <th></th>
                                      </tr>
                                    </tfoot>
                                  </Table>
                                </div>
                              </Col>
                            </Row>
                          )}

                          <Row className="mt-4">
                            <Col>
                              <Button
                                color="primary"
                                onClick={handleSave}
                                disabled={saveLoading || !form.CustomerId || !form.BusinessId || isReadOnly}
                              >
                                {saveLoading && <ClipLoader size={16} color="#ffffff" className="me-2" />}
                                Save Receipt
                              </Button>
                              <Button
                                color="secondary"
                                className="ms-2"
                                onClick={() => {
                                  setForm({
                                    ...defaultForm,
                                    BusinessId: session.companyId || null,
                                    BranchId: session.branchId || null,
                                    ReceiptDate: new Date(),
                                  });
                                  setPaymentAllocation([]);
                                  setCreditInvoices([]);
                                  setSelectedCustomerOption(null);
                                  loadNextReceiptNumber();
                                }}
                              >
                                Clear
                              </Button>
                            </Col>
                          </Row>
                        </Form>
                      </CardBody>
                    </Card>
                        )}
                      </div>
                    </TabPane>

                    <TabPane tabId="2">
                      <div>
                        {/* Receipts List */}
                        <Card>
                    <CardBody>
                      <div className="d-flex justify-content-between align-items-center mb-3">
                        <h5 className="card-title mb-0">Receipts List ({totalCount} records)</h5>
                        <Button
                          color="primary"
                          size="sm"
                          onClick={() => setShowFilters(!showFilters)}
                        >
                          {showFilters ? "Hide Filters" : "Show Filters"}
                        </Button>
                      </div>

                      {showFilters && (
                        <Row className="mb-3">
                          <Col md={3}>
                            <FormGroup>
                              <Label>Customer</Label>
                              <Input
                                type="text"
                                placeholder="Search customer..."
                              />
                            </FormGroup>
                          </Col>
                          <Col md={3}>
                            <FormGroup>
                              <Label>From Date</Label>
                              <DatePicker
                                selected={filter.FromDate}
                                onChange={(date) => setFilter(prev => ({ ...prev, FromDate: date }))}
                                dateFormat="dd/MM/yyyy"
                                className="form-control"
                                isClearable
                              />
                            </FormGroup>
                          </Col>
                          <Col md={3}>
                            <FormGroup>
                              <Label>To Date</Label>
                              <DatePicker
                                selected={filter.ToDate}
                                onChange={(date) => setFilter(prev => ({ ...prev, ToDate: date }))}
                                dateFormat="dd/MM/yyyy"
                                className="form-control"
                                isClearable
                              />
                            </FormGroup>
                          </Col>
                          <Col md={3}>
                            <FormGroup>
                              <Label>&nbsp;</Label>
                              <div>
                                <Button color="primary" size="sm" onClick={loadReceipts}>Apply</Button>
                                <Button color="secondary" size="sm" className="ms-2" onClick={() => {
                                  setFilter(emptyFilter);
                                  setPage(1);
                                  loadReceipts();
                                }}>Clear</Button>
                              </div>
                            </FormGroup>
                          </Col>
                        </Row>
                      )}

                      {loading ? (
                        <div className="text-center py-4">
                          <ClipLoader color="#556ee6" loading={loading} size={50} />
                        </div>
                      ) : (
                        <>
                          <div className="table-responsive">
                            <Table bordered>
                              <thead className="table-light">
                                <tr>
                                  <th>Sr.No</th>
                                  <th>Receipt Number</th>
                                  <th>Date</th>
                                  <th>Customer</th>
                                  <th>Amount</th>
                                  <th>Payment Method</th>
                                  <th>Actions</th>
                                </tr>
                              </thead>
                              <tbody>
                                {receipts.length === 0 ? (
                                  <tr>
                                    <td colSpan="7" className="text-center">No receipts found</td>
                                  </tr>
                                ) : (
                                  receipts.map((receipt, index) => {
                                    // Handle both camelCase and PascalCase from API
                                    const receiptId = receipt.receiptId || receipt.ReceiptId;
                                    const receiptNumber = receipt.receiptNumber || receipt.ReceiptNumber || 'N/A';
                                    const receiptDate = receipt.receiptDate || receipt.ReceiptDate;
                                    const amount = receipt.amount || receipt.Amount || 0;
                                    const paymentType = receipt.paymentType || receipt.PaymentType || 'ca';
                                    // Handle both camelCase and PascalCase for customer name
                                    const customerName = receipt.customer_NameEn || receipt.Customer_NameEn || receipt.customerNameEn || 'N/A';
                                    
                                    return (
                                      <tr key={receiptId}>
                                        <td>{(page - 1) * pageSize + index + 1}</td>
                                        <td>{receiptNumber}</td>
                                        <td>{formatDate(receiptDate)}</td>
                                        <td>{customerName}</td>
                                        <td>{formatCurrency(amount)}</td>
                                        <td>
                                          {paymentType === 'ca' ? 'Cash' : 
                                           paymentType === 'cc' ? 'Credit Card' : 'Online'}
                                        </td>
                                        <td>
                                          <Button
                                            color="primary"
                                            size="sm"
                                            onClick={() => handleView(receipt)}
                                            disabled={viewLoadingRow === receiptId}
                                          >
                                            {viewLoadingRow === receiptId ? (
                                              <ClipLoader size={16} color="#ffffff" />
                                            ) : (
                                              <Eye size={16} />
                                            )}
                                          </Button>
                                        </td>
                                      </tr>
                                    );
                                  })
                                )}
                              </tbody>
                            </Table>
                          </div>

                          {/* Pagination */}
                          {totalCount > pageSize && (
                            <div className="d-flex justify-content-between align-items-center mt-4 p-3" 
                                 style={{backgroundColor: '#f8f9fa', borderRadius: '0.375rem', border: '1px solid #dee2e6'}}>
                              <div className="text-muted" style={{fontSize: '14px', fontWeight: '500'}}>
                                Showing <span style={{color: '#556ee6', fontWeight: '600'}}>{totalCount > 0 ? (page - 1) * pageSize + 1 : 0}</span> to <span style={{color: '#556ee6', fontWeight: '600'}}>{Math.min(page * pageSize, totalCount)}</span> of <span style={{color: '#556ee6', fontWeight: '600'}}>{totalCount}</span> entries
                              </div>
                              <div style={{display: 'flex', alignItems: 'center', gap: '8px'}}>
                                <Button
                                  color="light"
                                  size="sm"
                                  disabled={page === 1}
                                  onClick={() => handlePageChange(page - 1)}
                                  style={{
                                    border: '1px solid #dee2e6',
                                    borderRadius: '0.375rem',
                                    minWidth: '32px',
                                    height: '32px',
                                  }}
                                >
                                  <i className="mdi mdi-chevron-left"></i>
                                </Button>
                                
                                {[...Array(Math.ceil(totalCount / pageSize))].map((_, index) => {
                                  const pageNum = index + 1;
                                  const isActive = page === pageNum;
                                  
                                  return (
                                    <Button
                                      key={pageNum}
                                      color={isActive ? "primary" : "light"}
                                      size="sm"
                                      onClick={() => handlePageChange(pageNum)}
                                      style={{
                                        border: '1px solid #dee2e6',
                                        borderRadius: '0.375rem',
                                        minWidth: '32px',
                                        height: '32px',
                                        backgroundColor: isActive ? '#556ee6' : '#fff',
                                        borderColor: isActive ? '#556ee6' : '#dee2e6',
                                        color: isActive ? '#fff' : '#495057',
                                      }}
                                    >
                                      {pageNum}
                                    </Button>
                                  );
                                })}
                                
                                <Button
                                  color="light"
                                  size="sm"
                                  disabled={page >= Math.ceil(totalCount / pageSize)}
                                  onClick={() => handlePageChange(page + 1)}
                                  style={{
                                    border: '1px solid #dee2e6',
                                    borderRadius: '0.375rem',
                                    minWidth: '32px',
                                    height: '32px',
                                  }}
                                >
                                  <i className="mdi mdi-chevron-right"></i>
                                </Button>
                              </div>
                            </div>
                          )}
                        </>
                      )}
                    </CardBody>
                  </Card>
                      </div>
                    </TabPane>
                  </TabContent>
                </CardBody>
              </Card>
            </Col>
          </Row>
        </Container>

        {/* Receipt Details Modal */}
        <Modal isOpen={showReceiptModal} toggle={() => setShowReceiptModal(false)} size="lg">
          <ModalHeader toggle={() => setShowReceiptModal(false)}>
            Receipt Details
          </ModalHeader>
          <ModalBody>
            {loadingReceiptDetails ? (
              <div className="text-center py-4">
                <ClipLoader color="#556ee6" loading={true} size={50} />
                <p className="mt-3">Loading receipt details...</p>
              </div>
            ) : receiptDetails ? (
              <div>
                {/* Receipt Header Information */}
                <Card className="mb-3 border-primary">
                  <CardBody>
                    <h6 className="card-title text-primary mb-3">Receipt Information</h6>
                    <Row>
                      <Col md={6}>
                        <p className="mb-2">
                          <strong>Receipt Number:</strong> {receiptDetails.receiptNumber || receiptDetails.ReceiptNumber || 'N/A'}
                        </p>
                        <p className="mb-2">
                          <strong>Date:</strong> {formatDate(receiptDetails.receiptDate || receiptDetails.ReceiptDate)}
                        </p>
                        <p className="mb-2">
                          <strong>Customer:</strong> {receiptDetails.customer_NameEn || receiptDetails.Customer_NameEn || 'N/A'}
                        </p>
                        <p className="mb-2">
                          <strong>Business:</strong> {receiptDetails.business_NameEn || receiptDetails.Business_NameEn || 'N/A'}
                        </p>
                      </Col>
                      <Col md={6}>
                        <p className="mb-2">
                          <strong>Branch:</strong> {receiptDetails.branch_NameEn || receiptDetails.Branch_NameEn || 'N/A'}
                        </p>
                        <p className="mb-2">
                          <strong>Total Amount:</strong> <span className="text-success fw-bold">{formatCurrency(receiptDetails.amount || receiptDetails.Amount || 0)}</span>
                        </p>
                        <p className="mb-2">
                          <strong>Payment Method:</strong> {
                            (receiptDetails.paymentType || receiptDetails.PaymentType || 'ca') === 'ca' ? 'Cash' : 
                            (receiptDetails.paymentType || receiptDetails.PaymentType) === 'cc' ? 'Credit Card' : 'Online'
                          }
                        </p>
                        {(receiptDetails.authCode || receiptDetails.AuthCode) && (
                          <p className="mb-2">
                            <strong>Auth Code:</strong> {receiptDetails.authCode || receiptDetails.AuthCode}
                          </p>
                        )}
                        <p className="mb-2">
                          <strong>Is Advance:</strong> {
                            (receiptDetails.is_Advance || receiptDetails.Is_Advance) ? (
                              <span className="badge bg-info">Yes</span>
                            ) : (
                              <span className="badge bg-secondary">No</span>
                            )
                          }
                        </p>
                      </Col>
                    </Row>
                  </CardBody>
                </Card>

                {/* Payment Allocation Details */}
                {receiptDetails.receiptDetails && receiptDetails.receiptDetails.length > 0 ? (
                  <Card className="mb-3 border-warning">
                    <CardBody>
                      <h6 className="card-title text-warning mb-3">Payment Allocation</h6>
                      <div className="table-responsive">
                        <Table bordered size="sm">
                          <thead className="table-light">
                            <tr>
                              <th>Invoice No</th>
                              <th>Invoice Date</th>
                              <th>Invoice Amount</th>
                              <th>Outstanding Before</th>
                              <th>Paid Amount</th>
                              <th>Status</th>
                            </tr>
                          </thead>
                          <tbody>
                            {receiptDetails.receiptDetails.map((detail, index) => {
                              console.log(`ReceiptDetail ${index}:`, detail);
                              
                              const invoiceAmount = detail.invoiceAmount || detail.InvoiceAmount || 0;
                              const paidAmount = detail.paidAmount || detail.PaidAmount || 0;
                              const totalAmount = detail.totalAmount || detail.TotalAmount || 0;
                              const isFullyPaid = paidAmount >= totalAmount;
                              
                              // Try multiple property name variations for invoice number
                              const invoiceNo = detail.invoice_No || detail.Invoice_No || detail.invoiceNo || detail.InvoiceNo || 
                                               detail.invoiceNumber || detail.InvoiceNumber || null;
                              
                              // Try multiple property name variations for invoice date
                              const invoiceDate = detail.invoice_Date || detail.Invoice_Date || detail.invoiceDate || 
                                                 detail.InvoiceDate || null;
                              
                              // If invoice number is still null, try to get it from InvoiceId if we have a mapping
                              // For now, show InvoiceId if no invoice number
                              const displayInvoiceNo = invoiceNo || (detail.invoiceId || detail.InvoiceId ? `Invoice #${detail.invoiceId || detail.InvoiceId}` : 'N/A');
                              
                              return (
                                <tr key={index}>
                                  <td>{displayInvoiceNo}</td>
                                  <td>
                                    {invoiceDate ? formatDate(invoiceDate) : 'N/A'}
                                  </td>
                                  <td>{formatCurrency(invoiceAmount)}</td>
                                  <td>{formatCurrency(totalAmount)}</td>
                                  <td className="fw-bold text-success">{formatCurrency(paidAmount)}</td>
                                  <td>
                                    {isFullyPaid ? (
                                      <span className="badge bg-success">Fully Paid</span>
                                    ) : (
                                      <span className="badge bg-warning text-dark">Partially Paid</span>
                                    )}
                                  </td>
                                </tr>
                              );
                            })}
                          </tbody>
                          <tfoot className="table-light">
                            <tr>
                              <th colSpan="4" className="text-end">Total Paid:</th>
                              <th className="text-success">
                                {formatCurrency(
                                  receiptDetails.receiptDetails.reduce(
                                    (sum, d) => sum + (d.paidAmount || d.PaidAmount || 0),
                                    0
                                  )
                                )}
                              </th>
                              <th></th>
                            </tr>
                          </tfoot>
                        </Table>
                      </div>
                    </CardBody>
                  </Card>
                ) : (
                  <Card className="mb-3 border-info">
                    <CardBody>
                      <div className="alert alert-info mb-0">
                        <i className="fas fa-info-circle me-2"></i>
                        This is an advance payment. No invoices were allocated to this receipt.
                      </div>
                    </CardBody>
                  </Card>
                )}

                {/* Summary Section */}
                <Card className="border-success">
                  <CardBody>
                    <h6 className="card-title text-success mb-3">Summary</h6>
                    <Row>
                      <Col md={6}>
                        <p className="mb-2">
                          <strong>Total Receipt Amount:</strong> <span className="text-success fw-bold">{formatCurrency(receiptDetails.amount || receiptDetails.Amount || 0)}</span>
                        </p>
                        {receiptDetails.receiptDetails && receiptDetails.receiptDetails.length > 0 && (
                          <p className="mb-2">
                            <strong>Invoices Paid:</strong> {receiptDetails.receiptDetails.length}
                          </p>
                        )}
                      </Col>
                      <Col md={6}>
                        {receiptDetails.receiptDetails && receiptDetails.receiptDetails.length > 0 && (
                          <p className="mb-2">
                            <strong>Total Allocated:</strong> <span className="text-primary fw-bold">
                              {formatCurrency(
                                receiptDetails.receiptDetails.reduce(
                                  (sum, d) => sum + (d.paidAmount || d.PaidAmount || 0),
                                  0
                                )
                              )}
                            </span>
                          </p>
                        )}
                        {(receiptDetails.is_Advance || receiptDetails.Is_Advance) && (
                          <p className="mb-2">
                            <strong>Advance Amount:</strong> <span className="text-info fw-bold">{formatCurrency(receiptDetails.amount || receiptDetails.Amount || 0)}</span>
                          </p>
                        )}
                      </Col>
                    </Row>
                  </CardBody>
                </Card>
              </div>
            ) : (
              <div className="text-center py-4">
                <p className="text-muted">No receipt details available</p>
              </div>
            )}
          </ModalBody>
          <ModalFooter>
            <Button color="secondary" onClick={() => setShowReceiptModal(false)}>
              Close
            </Button>
          </ModalFooter>
        </Modal>
      </div>
    </React.Fragment>
  );
}

